/**
 * Room code utilities
 */

/**
 * Generate a join URL for a room
 */
export function generateJoinUrl(roomName: string): string {
  return `${window.location.origin}/join?room=${roomName}`;
}

/**
 * Extract room code from URL query parameters
 */
export function getRoomCodeFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('room');
}

/**
 * Extract host room code from URL query parameters
 */
export function getHostCodeFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('host');
}

/**
 * Validate room code format (4 uppercase letters)
 */
export function isValidRoomCode(code: string): boolean {
  return /^[A-Z]{4}$/.test(code);
}

/**
 * Format room code (uppercase, remove spaces)
 */
export function formatRoomCode(code: string): string {
  return code.toUpperCase().replace(/\s/g, '').slice(0, 4);
}

/**
 * Generate a random 4-letter room code (A-Z). Caller must retry if code is taken.
 */
export function generateRoomCode(): string {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let code = '';
  for (let i = 0; i < 4; i++) {
    code += letters.charAt(Math.floor(Math.random() * letters.length));
  }
  return code;
}

/**
 * Remove a single query param from the URL without reloading.
 * Preserves other params (e.g. session_id). Use after consuming room/host for deep links.
 */
export function clearQueryParamFromUrl(name: 'room' | 'host' | 'premium-host'): void {
  const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams.has(name)) return;
  urlParams.delete(name);
  const newSearch = urlParams.toString();
  const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
  window.history.replaceState({}, '', newUrl);
}

/** Read ?room= from a backend/WebSocket URL (Cloudflare DO routing). */
export function getRoomFromBackendUrl(url: string): string | null {
  try {
    const room = new URL(url).searchParams.get('room');
    return room ? formatRoomCode(room) : null;
  } catch {
    return null;
  }
}
